import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import {
  OrderedCapacityExcess,
  OrderedCapacityExcessDetailed,
  OrderedCapacityExcessMeteringPoint,
  OrderedCapacityExcessesCalculation,
} from '@/features/orderedCapacityExcesses/types';
import { FiltersGrid, Pagination } from '@/features/types';
import { cachedItemByIdSelector, infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

export const orderedCapacityExcessesApi = createApi({
  reducerPath: 'orderedCapacityExcessesApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/ordered-capacity-excesses`,
  }),
  tagTypes: ['OrderedCapacityExcesses', 'OrderedCapacityExcess'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getOperatingExcesses: builder.infiniteQuery<
      OrderedCapacityExcess[],
      FiltersGrid & { name: string },
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '/query',
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: ['OrderedCapacityExcesses'],
    }),
    getOperatingExcessById: builder.query<
      OrderedCapacityExcessDetailed,
      string
    >({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: ['OrderedCapacityExcess'],
    }),
    getOperatingExcessesRecordsI: builder.infiniteQuery<
      OrderedCapacityExcessMeteringPoint[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '/records',
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: ['OrderedCapacityExcesses'],
    }),
    removeOperatingExcessById: builder.mutation<string, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => ['OrderedCapacityExcesses'],
    }),
    getIsExistsCalculationByOrganization: builder.query<
      OrderedCapacityExcessesCalculation,
      { year: number; month: number }
    >({
      query: data => ({
        url: `/is-exists-calculation-by-organization`,
        method: 'POST',
        data,
      }),
    }),
    calculateOperatingExcessByOrganization: builder.mutation<
      void,
      { year: number; month: number }
    >({
      query: data => ({
        url: `/calculate-by-organization`,
        method: 'POST',
        data,
      }),
    }),
    recalculateOperatingExcessByDocument: builder.mutation<string, string>({
      query: orderedCapacityExcessId => ({
        url: `/recalculate-by-document`,
        method: 'POST',
        data: { orderedCapacityExcessId },
      }),
      invalidatesTags: () => ['OrderedCapacityExcesses'],
    }),
  }),
});

export const {
  useGetOperatingExcessByIdQuery,
  useRemoveOperatingExcessByIdMutation,
  useGetIsExistsCalculationByOrganizationQuery,
  useLazyGetIsExistsCalculationByOrganizationQuery,
  useCalculateOperatingExcessByOrganizationMutation,
  useRecalculateOperatingExcessByDocumentMutation,
} = orderedCapacityExcessesApi;

export const useGetOperatingExcessesInfiniteQuery = normalizeInfiniteQueryData(
  orderedCapacityExcessesApi.useGetOperatingExcessesInfiniteQuery,
);

export const useGetOrderedCapacityExcessesRecordsInfiniteQuery =
  normalizeInfiniteQueryData(
    orderedCapacityExcessesApi.useGetOperatingExcessesRecordsIInfiniteQuery,
  );

const getCachedOperatingExcessByIdSelector = cachedItemByIdSelector(
  'orderedCapacityExcessesApi',
  'getOperatingExcesses',
);

export const useCachedOperatingExcessById = (id: string) =>
  useSelector(getCachedOperatingExcessByIdSelector(id));
